// views/NotFound.tsx
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiHome, HiArrowLeft } from "react-icons/hi2";

export default function NotFound() {
  const { t } = useTranslation();

  return (
    <div className="from-slate-50 dark:from-navy-950 dark:to-indigo-950/30 relative min-h-screen overflow-hidden bg-gradient-to-br via-white to-indigo-50/50 dark:via-navy-900">
      {/* Background Animated Gradients */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, -50, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute -left-40 -top-40 h-[500px] w-[500px] rounded-full bg-gradient-to-r from-indigo-200/30 to-purple-200/30 blur-3xl dark:from-indigo-500/10 dark:to-purple-500/10"
      />

      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, 50, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="from-rose-200/20 dark:from-rose-500/5 absolute -bottom-40 -right-40 h-[600px] w-[600px] rounded-full bg-gradient-to-r to-orange-200/20 blur-3xl dark:to-orange-500/5"
      />

      {/* Main Content */}
      <div className="relative flex min-h-screen items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-full max-w-2xl text-center"
        >
          {/* 404 Number */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.7, ease: "easeOut" }}
            className="relative mb-6"
          >
            <div className="relative inline-block">
              {/* Glow Effect */}
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)",
                }}
              />

              <span className="text-transparent relative inline-block bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-[120px] font-black leading-none dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 sm:text-[160px] md:text-[200px]">
                404
              </span>
            </div>
          </motion.div>

          {/* Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              delay: 0.3,
              duration: 0.6,
              type: "spring",
              bounce: 0.5,
            }}
            className="mb-6 flex justify-center"
          >
            <div className="rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 p-4 dark:from-indigo-900/30 dark:to-purple-900/30">
              <svg
                className="h-14 w-14 text-indigo-500 dark:text-indigo-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mb-3 text-2xl font-bold text-navy-700 dark:text-white sm:text-3xl"
          >
            {t("notFound.title")}
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mx-auto mb-8 max-w-md text-sm text-gray-500 dark:text-gray-400 sm:text-base"
          >
            {t("notFound.description")}
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4"
          >
            <Link to="/">
              <button className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-indigo-500/35 sm:w-auto">
                <HiHome className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5" />
                <span>{t("notFound.backToHome")}</span>
              </button>
            </Link>

            <button
              onClick={() => window.history.back()}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-gray-200 bg-white/50 px-8 py-3 text-sm font-medium text-gray-700 transition-all duration-300 hover:scale-105 hover:border-indigo-300 hover:bg-indigo-50/50 dark:border-navy-600 dark:bg-navy-700/50 dark:text-gray-300 dark:hover:border-indigo-500/50 dark:hover:bg-navy-700 sm:w-auto"
            >
              <HiArrowLeft className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1" />
              <span>{t("notFound.goBack")}</span>
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
